import React, { useState, useEffect } from 'react';
import { enquiriesApi, templatesApi } from '../api/client';
import { Badge, Modal, Spin, useFlash, F } from '../components/UI';
import { Send } from '../components/Icons';
import CustomerSearch from '../components/CustomerSearch';

// ── WhatsApp helpers ──────────────────────────────────────────────────────────
const openWA = (phone, msg) => {
  const n = String(phone || '').replace(/\D/g, '');
  const num = n.length === 10 ? `91${n}` : n;
  if (!num) return;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
};

const fillTpl = (body, name, extras = {}) =>
  body
    .replace(/\[Customer Name\]/g, name || 'Customer')
    .replace(/\[Service ID\]/g,    extras.serviceId || '')
    .replace(/\[Product\]/g,       extras.product   || '');

const matchEnqTpl = (tpls, productInterest) => {
  const p = (productInterest || '').toLowerCase();
  const find = kw => tpls.find(t => t.name.toLowerCase().includes(kw));
  if (p === 'solar inverter' || p.includes('solar inverter')) return find('solar inverter') || find('solar');
  if (p === 'solar panel'    || p.includes('solar panel'))   return find('solar') || find('solar inverter');
  if (p.includes('battery'))                                 return find('battery');
  if (p.includes('ups'))                                     return find('ups');
  if (p.includes('inverter'))                                return find('solar inverter') || find('inverter');
  return find('general') || tpls.find(t => t.type === 'Enquiry') || tpls[0];
};

// ─────────────────────────────────────────────────────────────────────────────

export default function Enquiry() {
  const [tab, setTab]   = useState('list');
  const [enqs, setEnqs] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [busy, setBusy] = useState(true);
  const [selEnq, setSel]  = useState(null);
  const [viewEnq, setView] = useState(null);
  const [tplId, setTplId] = useState('');
  const { show, Flash }   = useFlash();
  const [custType,  setCustType]  = useState('walkin');
  const [cFound,    setCFound]    = useState(null);
  const [form, setForm]   = useState({ visitor_name:'', visitor_phone:'', visitor_address:'', product_interest:'Inverter', source:'Walk-in', notes:'' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const pickCustomer = c => {
    setCFound(c);
    setForm(f => ({
      ...f,
      visitor_name:    c.full_name || '',
      visitor_phone:   c.phone     || '',
      visitor_address: [c.address, c.district].filter(Boolean).join(', '),
    }));
  };

  useEffect(() => {
    Promise.all([enquiriesApi.list(), templatesApi.list()])
      .then(([{ data: e }, { data: t }]) => { setEnqs(e); setTpls(t); })
      .finally(() => setBusy(false));
  }, []);

  // Auto-select template when enquiry is selected from list
  const openSend = enq => {
    setSel(enq);
    const tpl = matchEnqTpl(tpls, enq.product_interest);
    setTplId(tpl?.id || '');
  };

  const saveNew = async () => {
    if (!form.visitor_name || !form.visitor_phone) { show('Name and phone required.', 'error'); return; }
    try {
      const payload = { ...form, customer_id: cFound?.id || null };
      const { data } = await enquiriesApi.create(payload);
      setEnqs(es => [data, ...es]);
      show('Enquiry saved!');
      setTab('list');

      // Auto-match template and send WhatsApp
      const tpl = matchEnqTpl(tpls, form.product_interest);
      if (tpl) {
        const msg = fillTpl(tpl.body, form.visitor_name, { product: form.product_interest });
        openWA(form.visitor_phone, msg);
        enquiriesApi.update(data.id, { template_sent: true, template_id: tpl.id, status: 'contacted' }).catch(() => {});
        setEnqs(es => es.map(e => e.id === data.id ? { ...e, template_sent: true, status: 'contacted' } : e));
      }

      setForm({ visitor_name:'', visitor_phone:'', visitor_address:'', product_interest:'Inverter', source:'Walk-in', notes:'' });
      setCustType('walkin'); setCFound(null);
    } catch { show('Failed to save enquiry.', 'error'); }
  };

  const sendTpl = async () => {
    if (!tplId || !selEnq?.id) return;
    const tpl = tpls.find(t => t.id === tplId);
    await enquiriesApi.update(selEnq.id, { template_sent: true, template_id: tplId, status: 'contacted' });
    setEnqs(es => es.map(e => e.id === selEnq.id ? { ...e, template_sent: true, status: 'contacted' } : e));
    if (tpl && selEnq.visitor_phone) {
      const msg = fillTpl(tpl.body, selEnq.visitor_name, { product: selEnq.product_interest });
      openWA(selEnq.visitor_phone, msg);
    }
    show('Template sent via WhatsApp!');
    setSel(null);
  };

  const selTpl  = tpls.find(t => t.id === tplId);
  const preview = selTpl ? fillTpl(selTpl.body, selEnq?.visitor_name || 'Customer', { product: selEnq?.product_interest }) : '';

  if (busy) return <Spin/>;
  return (
    <div>
      <Flash/>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' on' : ''}`} onClick={() => setTab('list')}>All Enquiries</button>
        <button className={`tab${tab === 'new'  ? ' on' : ''}`} onClick={() => setTab('new')}>New Enquiry</button>
      </div>

      {tab === 'list' && (
        <div className="card card-0">
          <div className="tbl-wrap"><table>
            <thead><tr><th>Date</th><th>Name</th><th>Phone</th><th>Interest</th><th>Source</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {enqs.map(e => (
                <tr key={e.id} style={{ cursor:'pointer' }} onClick={() => setView(e)}>
                  <td style={{ fontSize:12 }}>{new Date(e.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontWeight:500 }}>{e.visitor_name || '—'}</td>
                  <td>{e.visitor_phone || '—'}</td>
                  <td>{e.product_interest || '—'}</td>
                  <td>{e.source}</td>
                  <td><Badge label={e.status}/></td>
                  <td onClick={ev => ev.stopPropagation()}>
                    <button className="btn btn-sm" disabled={e.status === 'converted'} onClick={() => openSend(e)}>
                      <Send s={13}/> Send Template
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {tab === 'new' && (
        <div className="card">
          {/* Customer type toggle */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontWeight:600, fontSize:11, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:7 }}>
              Customer Type
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {[['walkin','Walk-in / New Visitor'],['existing','Existing Customer']].map(([t,lbl]) => (
                <button key={t} className={`btn btn-sm${custType===t?' btn-primary':''}`}
                  onClick={() => { setCustType(t); setCFound(null); setForm(f => ({ ...f, visitor_name:'', visitor_phone:'', visitor_address:'' })); }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {custType === 'existing' && (
            !cFound
              ? <div style={{ marginBottom:12 }}>
                  <CustomerSearch onSelect={pickCustomer} placeholder="Search by name / phone / customer ID…"/>
                </div>
              : <div style={{ marginBottom:12, background:'var(--surface)', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div>
                    <div style={{ fontWeight:700, color:'var(--navy)', marginBottom:2 }}>{cFound.full_name}</div>
                    {cFound.phone   && <div style={{ fontSize:12, color:'var(--text2)' }}>{cFound.phone}</div>}
                    {cFound.address && <div style={{ fontSize:12, color:'var(--text2)' }}>{[cFound.address, cFound.district].filter(Boolean).join(', ')}</div>}
                  </div>
                  <button className="btn btn-sm btn-ghost" style={{ padding:'2px 8px', flexShrink:0 }} onClick={() => { setCFound(null); setForm(f => ({ ...f, visitor_name:'', visitor_phone:'', visitor_address:'' })); }}>Change</button>
                </div>
          )}

          {custType === 'walkin' && (
            <div className="notice n-info" style={{ marginBottom:14 }}>
              For unregistered visitors — basic details only required. A WhatsApp message will be sent automatically on save.
            </div>
          )}

          <div className="fg-2" style={{ marginBottom:14 }}>
            <F label="Full Name" req><input value={form.visitor_name} onChange={set('visitor_name')} placeholder="Visitor name"/></F>
            <F label="Phone Number" req><input value={form.visitor_phone} onChange={set('visitor_phone')} placeholder="10-digit mobile"/></F>
            <F label="Address" full><input value={form.visitor_address} onChange={set('visitor_address')} placeholder="Area / locality"/></F>
            <F label="Product Interest">
              <select value={form.product_interest} onChange={set('product_interest')}>
                {['Inverter','Battery','UPS','Solar Inverter','Solar Panel'].map(p => <option key={p}>{p}</option>)}
              </select>
            </F>
            <F label="Source">
              <select value={form.source} onChange={set('source')}>
                {['Walk-in','Phone','WhatsApp','Referral'].map(s => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Notes" full><textarea value={form.notes} onChange={set('notes')} placeholder="Enquiry details…"/></F>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button className="btn btn-primary" onClick={saveNew}>Save Enquiry &amp; Send WhatsApp</button>
            <span style={{ fontSize:12, color:'var(--text2)' }}>
              Template: <strong>{matchEnqTpl(tpls, form.product_interest)?.name || 'General Enquiry'}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Enquiry detail modal */}
      <Modal open={!!viewEnq} onClose={() => setView(null)} title="Enquiry Details"
        footer={
          <>
            <button className="btn" onClick={() => setView(null)}>Close</button>
            {viewEnq && viewEnq.status !== 'converted' && (
              <button className="btn btn-primary" onClick={() => { openSend(viewEnq); setView(null); }}>
                <Send s={13}/> Send Template
              </button>
            )}
          </>
        }
      >
        {viewEnq && (() => {
          const rows = [
            ['Name',            viewEnq.visitor_name    || '—'],
            ['Phone',           viewEnq.visitor_phone   || '—'],
            ['Address',         viewEnq.visitor_address || '—'],
            ['Product Interest',viewEnq.product_interest|| '—'],
            ['Source',          viewEnq.source          || '—'],
            ['Status',          null],
            ['Notes',           viewEnq.notes           || '—'],
            ['Date',            new Date(viewEnq.created_at).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })],
          ];
          return (
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {rows.map(([label, value]) => (
                <div key={label} style={{ display:'flex', gap:12, padding:'9px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                  <span style={{ minWidth:130, fontWeight:600, color:'var(--text2)', fontSize:12 }}>{label}</span>
                  {label === 'Status'
                    ? <Badge label={viewEnq.status}/>
                    : <span style={{ flex:1, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{value}</span>
                  }
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>

      {/* Manual template send modal */}
      <Modal open={!!selEnq} onClose={() => setSel(null)} title="Send WhatsApp Template"
        footer={
          <>
            <button className="btn" onClick={() => setSel(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={sendTpl} disabled={!tplId || !selEnq?.visitor_phone}>
              <Send s={14}/> Open WhatsApp
            </button>
          </>
        }
      >
        {selEnq && !selEnq.visitor_phone && (
          <div className="notice n-warn" style={{ marginBottom:12 }}>No phone number on record for this enquiry.</div>
        )}
        <div className="fg-2">
          <F label="Select Template" full>
            <select value={tplId} onChange={e => setTplId(e.target.value)}>
              <option value="">-- Select --</option>
              {tpls.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </F>
        </div>
        {preview && (
          <F label="Message Preview">
            <textarea readOnly value={preview} style={{ minHeight:130, fontSize:12, background:'var(--surface)' }}/>
          </F>
        )}
      </Modal>
    </div>
  );
}
